import React from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { Mic, Trash2 } from 'lucide-react';

/**
 * KanbanDropZone — Action drop zones shown during drag
 *
 * Rendered as a flex row BELOW the columns container (not fixed-position).
 * Always mounted so Droppables are registered before drag starts.
 *
 * IMPORTANTE (performance/estabilidade do drag): a faixa tem ALTURA FIXA
 * (espaço pré-reservado) e só o conteúdo faz fade de opacity. Nada de
 * maxHeight/padding animados — mudança de layout durante o arrasto
 * invalida a geometria cacheada do @hello-pangea/dnd e trava o drag.
 */
const KanbanDropZone = ({ draggedVideo, isDragging }) => {
  const { t } = useTranslation();
  const isVisible = isDragging && !draggedVideo?.is_imported;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        padding: '12px 16px',
        height: '80px',
        boxSizing: 'border-box',
        transition: 'opacity 0.2s ease',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      {/* Audio zone */}
      <Droppable droppableId="ACTION_GENERATE_AUDIO">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1,
              maxWidth: '280px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '14px 20px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              transition: 'all 0.2s ease',
              transform: snapshot.isDraggingOver ? 'scale(1.03)' : 'scale(1)',
              background: snapshot.isDraggingOver
                ? 'rgba(59, 130, 246, 0.25)'
                : 'var(--dp-bg-elevated, rgba(30, 41, 59, 0.95))',
              border: `2px dashed ${
                snapshot.isDraggingOver
                  ? '#3B82F6'
                  : 'var(--dp-border-medium, rgba(71, 85, 105, 0.5))'
              }`,
              color: snapshot.isDraggingOver
                ? '#3B82F6'
                : 'var(--dp-text-secondary, #9CA3AF)',
              boxShadow: snapshot.isDraggingOver
                ? '0 4px 16px rgba(59, 130, 246, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Mic size={18} />
            <span>{t('kanban.drop_to_generate_audio')}</span>
            <div style={{ display: 'none' }}>{provided.placeholder}</div>
          </div>
        )}
      </Droppable>

      {/* Delete zone */}
      <Droppable droppableId="ACTION_DELETE">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              flex: 1,
              maxWidth: '280px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '14px 20px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              transition: 'all 0.2s ease',
              transform: snapshot.isDraggingOver ? 'scale(1.03)' : 'scale(1)',
              background: snapshot.isDraggingOver
                ? 'rgba(239, 68, 68, 0.25)'
                : 'var(--dp-bg-elevated, rgba(30, 41, 59, 0.95))',
              border: `2px dashed ${
                snapshot.isDraggingOver
                  ? '#EF4444'
                  : 'var(--dp-border-medium, rgba(71, 85, 105, 0.5))'
              }`,
              color: snapshot.isDraggingOver
                ? '#EF4444'
                : 'var(--dp-text-secondary, #9CA3AF)',
              boxShadow: snapshot.isDraggingOver
                ? '0 4px 16px rgba(239, 68, 68, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Trash2 size={18} />
            <span>{t('kanban.drop_to_delete')}</span>
            <div style={{ display: 'none' }}>{provided.placeholder}</div>
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default KanbanDropZone;
